import { useEffect, useMemo, useState } from 'react';
import { ExternalLink, MessageCircle, Send, Square, Trash2 } from 'lucide-react';
import useStreamingAutoScroll from '../../hooks/useStreamingAutoScroll';
import MiniMarkdown from '../../utils/miniMarkdown';
import styles from './DeepAnalysisChat.module.css';

export default function DeepAnalysisChat({
  chat,
  busy,
  suggestions,
  onAsk,
  onStop,
  onClear,
  onInspectCorner,
}) {
  const [question, setQuestion] = useState('');
  const contentSignature = useMemo(() => (
    (chat.messages || []).map(message => `${message.id}:${message.text}`).join('|')
  ), [chat.messages]);
  const scroll = useStreamingAutoScroll({
    status: chat.status === 'checking' ? 'correcting' : chat.status,
    content: contentSignature,
  });

  useEffect(() => {
    if (!chat.messages.length && scroll.containerRef.current) {
      scroll.containerRef.current.scrollTop = 0;
    }
  }, [chat.messages.length, scroll.containerRef]);

  const submit = value => {
    const next = String(value ?? question).trim();
    if (!next || busy) return;
    setQuestion('');
    onAsk(next);
  };

  return (
    <aside className={styles.chat} aria-label="Chat with Deep Analysis">
      <header className={styles.header}>
        <div>
          <span><MessageCircle size={15} /> Follow-up coach</span>
          <small>Evidence first · broader advice clearly labeled</small>
        </div>
        {!!chat.messages.length && (
          <button type="button" onClick={onClear} disabled={busy} title="Clear conversation">
            <Trash2 size={14} /> Clear
          </button>
        )}
      </header>

      <div
        className={styles.transcript}
        ref={scroll.containerRef}
        tabIndex={0}
        onScroll={scroll.onScroll}
        onWheel={scroll.onWheel}
        onTouchStart={scroll.onTouchStart}
        onTouchMove={scroll.onTouchMove}
        onKeyDown={scroll.onKeyDown}
      >
        {!chat.messages.length && (
          <div className={styles.starter}>
            <strong>Ask about what the analysis found</strong>
            <span>I’ll keep measured conclusions separate from general coaching guidance.</span>
            <div className={styles.suggestions}>
              {(suggestions || []).map(suggestion => (
                <button
                  key={suggestion}
                  type="button"
                  // submit() already ignores a click while busy. Saying so is
                  // the point: an enabled button that silently does nothing
                  // reads as broken.
                  disabled={busy}
                  onClick={() => submit(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {(chat.messages || []).map((message, index) => (
          <article
            key={message.id}
            className={`${styles.message} ${styles[message.role]}`}
            aria-live={message.status === 'streaming' ? 'polite' : undefined}
          >
            <span className={styles.role}>{message.role === 'user' ? 'You' : 'Coach'}</span>
            {message.error ? (
              <div className={styles.messageError}>
                <span>{message.error}</span>
                {message.role === 'assistant' && chat.messages[index - 1]?.role === 'user' && (
                  <button type="button" onClick={() => submit(chat.messages[index - 1].text)}>
                    Retry question
                  </button>
                )}
              </div>
            ) : message.role === 'assistant' ? (
              <>
                <MiniMarkdown text={message.text} className={styles.answer} />
                {message.status === 'streaming' && <span className={styles.typing}>Analyzing…</span>}
                {!!message.cornerRefs?.length && (
                  <div className={styles.cornerRefs}>
                    {message.cornerRefs.map(corner => (
                      <button
                        key={corner.cornerId}
                        type="button"
                        onClick={() => onInspectCorner(corner.cornerId)}
                      >
                        Inspect {corner.label} <ExternalLink size={12} />
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p>{message.text}</p>
            )}
          </article>
        ))}

        {chat.status === 'checking' && (
          <div className={styles.checking}>Checking the answer against measured evidence…</div>
        )}
        {!!chat.trimmedPairs && (
          <div className={styles.trimmed}>
            Earlier turns remain visible but are no longer included in Mistral’s context.
          </div>
        )}
        {chat.error && <div className={styles.messageError}>{chat.error}</div>}
      </div>

      <div className={styles.composer}>
        <label htmlFor="deep-analysis-question">Ask your own question</label>
        <textarea
          id="deep-analysis-question"
          value={question}
          maxLength={1000}
          rows={3}
          disabled={busy}
          placeholder="Ask why a corner is prioritized, what supports a finding, or how to validate the change…"
          aria-label="Ask a follow-up question"
          onChange={event => setQuestion(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
        />
        {busy ? (
          <button type="button" className={styles.stop} onClick={onStop}>
            <Square size={13} fill="currentColor" /> Stop
          </button>
        ) : (
          <button type="button" onClick={() => submit()} disabled={!question.trim()}>
            <Send size={14} /> Ask
          </button>
        )}
      </div>
    </aside>
  );
}
